import React from "react";
import ReactDOM from "react-dom/client";
import App from "@/App.jsx";
// KaTeX styles for all et-ui math rendering. Apps must load this once at the
// entry point; never import katex CSS directly in feature code.
import "@ever-tutor/et-ui/style.css";
import "./index.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { initDatadog } from "./utils/datadog-utils";
import { initRenderHealth } from "./utils/renderHealth";
import { installTranslationDomGuard } from "./utils/pageTranslation";
import { initMixpanel } from "./utils/mixpanel-utils";

// Early preconnect to API and WebSocket origins to warm TCP+TLS
// Reduces ~100-300ms latency on first connection by parallelizing handshake
function addPreconnect(url, crossOrigin = "") {
  if (!url) return;
  try {
    const origin = new URL(url).origin;
    const link = document.createElement("link");
    link.rel = "preconnect";
    link.href = origin;
    if (crossOrigin) link.crossOrigin = crossOrigin;
    document.head.appendChild(link);
  } catch {
    // Invalid URL, skip
  }
}
// Use 'use-credentials' to match fetch credentials:include mode for connection reuse
// Three origins now: apps/web serves most of the API, apps/tutor the session
// endpoints, and the WebSocket its own host.
addPreconnect(import.meta.env.VITE_WEB_BASE_URL, "use-credentials");
addPreconnect(import.meta.env.VITE_BASE_URL, "use-credentials");
addPreconnect(import.meta.env.VITE_WS_BASE_URL, "use-credentials");

// Must run before the first render: page-translation extensions rewrite text
// nodes under React-owned parents, which otherwise crashes the commit phase
// with "insertBefore ... is not a child of this node".
installTranslationDomGuard();

// Read and taken out of the address bar before Datadog and Mixpanel start and
// before the first render. Both record the first view's URL on init, and a
// hand-written link may carry a password, which the overlay refuses but the URL
// still holds. HubSpot's async tracker in index.html can read the URL earlier;
// this does not try to beat it. The router may redirect (to /login, say)
// and take a ?qa= link with it, and one that saw qa would write it back with
// its next search-param update.
let devOverlayBootSearch = "";
if (__DEV_OVERLAY__) {
  devOverlayBootSearch = window.location.search;
  const bootUrl = new URL(window.location.href);
  if (bootUrl.searchParams.has("qa")) {
    bootUrl.searchParams.delete("qa");
    window.history.replaceState(window.history.state, "", bootUrl);
  }
}

initDatadog();
initRenderHealth();
initMixpanel();

ReactDOM.createRoot(document.getElementById("root")).render(<App />);

// __DEV_OVERLAY__ is a build-time literal: false on every bundle but the
// development branch's, which drops this import and its chunk entirely.
// vite.config.ts fails the build if an overlay module still gets in.
if (__DEV_OVERLAY__) {
  import("./devOverlay/devOverlayMount")
    .then(({ mountDevOverlay }) => mountDevOverlay(devOverlayBootSearch))
    .catch((error) => console.warn("[dev overlay] failed to load:", error));
}
